import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '../../lib/sentryBuffer';

/**
 * Route-level error boundary for React Router's `errorElement`.
 * Handles both route error responses (404, etc.) and unexpected errors thrown inside routes.
 *
 * @example
 * // In router config:
 * { path: '/', element: <Home />, errorElement: <RouteErrorBoundary /> }
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Report unexpected errors to Sentry
  if (!isRouteErrorResponse(error)) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    captureException(errorObj);
    console.error('RouteErrorBoundary caught an error:', errorObj);
  }

  const handleGoHome = useCallback(() => {
    navigate('/app', { replace: true });
  }, [navigate]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  // 404 - Not Found
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-sky-50 to-purple-50 dark:from-dark-bg dark:to-slate-900">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-dark-text mb-2 font-heebo">
            {t('errors.pageNotFound')}
          </h1>
          <p className="text-gray-600 dark:text-dark-muted mb-6 font-heebo">
            {t('errors.pageNotFoundDescription')}
          </p>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-kid-purple hover:bg-purple-600 text-white rounded-xl font-bold shadow-lg transition-colors font-heebo"
          >
            {t('errors.goHome')}
          </button>
        </div>
      </div>
    );
  }

  // Generic error fallback
  const errorMessage = error instanceof Error ? error.message : t('errors.unknownError');
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-red-50 to-orange-50 dark:from-dark-bg dark:to-slate-900">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">😢</div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-dark-text mb-2 font-heebo">
          {t('errors.somethingWentWrong')}
        </h1>
        <p className="text-gray-600 dark:text-dark-muted mb-6 font-heebo">
          {t('errors.somethingWentWrongDescription')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleReload}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold shadow-lg transition-colors font-heebo"
          >
            {t('errors.reload')}
          </button>
          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-dark-text rounded-xl font-bold shadow-lg transition-colors font-heebo"
          >
            {t('errors.goHome')}
          </button>
        </div>

        {import.meta.env.DEV && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400">
              {t('errors.devDetails')}
            </summary>
            <pre className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs overflow-auto max-h-48 text-red-600 dark:text-red-400">
              {errorMessage}
              {errorStack && `\n\n${errorStack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};
